/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { type CommunityType, getFullName } from "@packages/sdk";
import {
  Avatar,
  Button,
  CloseIcon,
  IconButton,
  Skeleton,
  spacing,
  Text,
  useErrorNotification,
  useIntersectionObserverRefState,
  useThemeValues,
} from "@packages/ui";
import { useEffect, useRef } from "react";

import { useRequestCommunitiesAdminUsersPending } from "../../../../requests/useRequestCommunitiesAdminUsersPending";
import { useRequestCommunityMemberApprove } from "../../../../requests/useRequestCommunityMemberApprove";
import { useRequestCommunityMemberDelete } from "../../../../requests/useRequestCommunityMemberDelete";

const listStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  paddingBottom: spacing.xxxl,
  width: "100%",
};

const rowStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
  paddingBottom: spacing.m,
  paddingTop: spacing.m,
};

const nameContainerStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  gap: spacing.xxs,
  minWidth: 0,
};

const actionsStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

const emptyStateStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
  minHeight: "200px",
  padding: spacing.xl,
};

const skeletonNameStyles: Interpolation = {
  height: 16,
  width: 160,
};

export type RequestsListProps = {
  communityId: number;
  communityType: CommunityType;
};

export const RequestsList = ({
  communityId,
  communityType,
}: RequestsListProps) => {
  const { track } = useAnalytics();
  const t = useTranslations();
  const { semanticColors } = useThemeValues();
  const infiniteScrollBufferRef = useRef<HTMLDivElement>(null);

  const requestsInfiniteQuery = useRequestCommunitiesAdminUsersPending({
    communityId,
  });

  useErrorNotification({ isError: requestsInfiniteQuery.isError });

  const users =
    requestsInfiniteQuery.data?.pages.flatMap((page) => page.results) ?? [];

  const [infiniteScrollBufferRefShowing] = useIntersectionObserverRefState({
    deps: [users.length],
    ref: infiniteScrollBufferRef,
  });

  const fetchNextPage = requestsInfiniteQuery.fetchNextPage;
  const shouldFetchNextPage =
    infiniteScrollBufferRefShowing &&
    requestsInfiniteQuery.hasNextPage &&
    !requestsInfiniteQuery.isFetchingNextPage;

  useEffect(() => {
    if (shouldFetchNextPage) fetchNextPage();
  }, [shouldFetchNextPage, fetchNextPage]);

  const approveMutation = useRequestCommunityMemberApprove({
    id: communityId,
  });
  const rejectMutation = useRequestCommunityMemberDelete({ id: communityId });

  const trackTap = (action: "accept" | "reject") => {
    track({
      event: HallowAnalyticsEvent.TappedGroupJoinRequest,
      properties: {
        community_id: communityId,
        action,
        type: communityType,
      },
    });
  };

  const handleAccept = (userId: number) => {
    trackTap("accept");
    approveMutation.mutate({
      params: { path: { community_id: communityId, user_id: userId } },
    });
  };

  const handleReject = (userId: number) => {
    trackTap("reject");
    rejectMutation.mutate({
      params: { path: { community_id: communityId, user_id: userId } },
    });
  };

  const inFlightUserId = (mutation: {
    isPending: boolean;
    variables?: { params?: { path?: { user_id?: number } } };
  }) => (mutation.isPending ? mutation.variables?.params?.path?.user_id : null);

  const approvingUserId = inFlightUserId(approveMutation);
  const rejectingUserId = inFlightUserId(rejectMutation);

  if (requestsInfiniteQuery.isLoading) {
    return (
      <div css={listStyles}>
        {Array.from({ length: 5 }).map((_, index) => (
          <div css={rowStyles} key={index}>
            <Avatar size={48} loading />
            <div css={nameContainerStyles}>
              <Skeleton css={skeletonNameStyles} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div css={emptyStateStyles}>
        <Text as="p" type="body" size="m">
          <I18nMessage message="community_members_requests_empty" />
        </Text>
      </div>
    );
  }

  return (
    <div css={listStyles}>
      {users.map((user) => {
        const isBusy =
          approvingUserId === user.id || rejectingUserId === user.id;

        return (
          <div css={rowStyles} key={user.id}>
            <Avatar imgSrc={user.image_url} size={48} />
            <div css={nameContainerStyles}>
              <Text type="title" size="s" overflow="ellipsis">
                {getFullName({
                  firstName: user.name,
                  // `last_name` is typed `unknown` in the generated schema, so
                  // narrow it to a string here (mirrors how `username` is
                  // handled below) before combining into the full name.
                  lastName:
                    typeof user.last_name === "string"
                      ? user.last_name
                      : undefined,
                })}
              </Text>
              {typeof user.username === "string" && (
                <Text
                  type="body"
                  size="s"
                  overflow="ellipsis"
                  css={{ color: semanticColors.neutralsMedium }}
                >
                  @{user.username}
                </Text>
              )}
            </div>
            <div css={actionsStyles}>
              <Button
                variant="primary"
                size="s"
                disabled={isBusy}
                isLoading={approvingUserId === user.id}
                onClick={() => handleAccept(user.id)}
              >
                <I18nMessage message="community_members_requests_accept" />
              </Button>
              <IconButton
                variant="neutral"
                size="s"
                disabled={isBusy}
                aria-label={t("community_members_requests_reject")}
                icon={<CloseIcon />}
                onClick={() => handleReject(user.id)}
              />
            </div>
          </div>
        );
      })}
      {requestsInfiniteQuery.hasNextPage && (
        <div ref={infiniteScrollBufferRef} />
      )}
    </div>
  );
};
